'use strict';

import assign from 'object-assign';
import { EventEmitter } from 'events';
import qs from 'qs';
import cookie from 'cookie-monster';
import store from 'store';
import moment from 'moment';
import * as Sentry from '@sentry/react';
// Dynamic import for Capacitor Preferences - will be loaded only when available

import UserConstants from '@constants/UserConstants';
import AuthConstants from '@constants/AuthConstants';
import AppDispatcher from '@app/dispatcher/AppDispatcher';
import Analytics from '@utils/Analytics';
import MealStore from '@stores/MealStore';
import GroceryStore from '@stores/GroceryStore';

import { getConfig } from '@utils/Env';
import SessionRecorder from '@utils/SessionRecorder';
import { tokenClaims } from '@utils/TokenClaims';
import { fetchWithRetry } from '@utils/FetchRetry';

const CHANGE_EVENT = 'change';
const COOKIE_KEY_PREFIX = 'auth-tokens-';
const TOKEN_KEY_PREFIX = 'auth-tokens-';

let _auth = {
    links: store.get(COOKIE_KEY_PREFIX + 'links'),
    storage: store.get('auth-storage-mode') || 'cookie',
};

// Cache for Capacitor Preferences - loaded dynamically
let _capacitorPreferences = null;
let _capacitorAvailable = null;

/**
 * Check if Capacitor Preferences is available and cache the result
 * @returns {Promise<boolean>} True if Capacitor Preferences is available
 */
async function isCapacitorPreferencesAvailable() {
    if (_capacitorAvailable !== null) {
        return _capacitorAvailable;
    }

    try {
        // Try to dynamically import Capacitor Preferences
        const capacitorModule = await import('@capacitor/preferences');
        _capacitorPreferences = capacitorModule.Preferences;
        
        // Test if the plugin actually works
        await _capacitorPreferences.get({ key: 'test-capacitor-availability' });
        
        _capacitorAvailable = true;
        Sentry.addBreadcrumb({
            message: 'Capacitor Preferences plugin is available',
            category: 'auth.capacitor',
            level: 'info',
        });
        return true;
    } catch (error) {
        _capacitorAvailable = false;
        Sentry.addBreadcrumb({
            message: 'Capacitor Preferences plugin is not available, using localStorage fallback',
            category: 'auth.capacitor',
            level: 'info',
            data: { error: error.message },
        });
        return false;
    }
}

/**
 * Sync token to Capacitor Preferences in background (fire-and-forget)
 * @param {string} name - Token name
 * @param {string} token - Token value
 * @param {moment.Moment} expires - Token expiration date
 */
async function syncToCapacitorPreferences(name, token, expires) {
    try {
        const capacitorAvailable = await isCapacitorPreferencesAvailable();
        if (capacitorAvailable && _capacitorPreferences) {
            const tokenData = JSON.stringify({
                token,
                expires: expires.toDate().getTime(),
            });
            await _capacitorPreferences.set({
                key: TOKEN_KEY_PREFIX + name,
                value: tokenData,
            });
        }
    } catch (error) {
        // Silent failure - this is fire-and-forget background sync
        Sentry.addBreadcrumb({
            message: 'Background sync to Capacitor Preferences failed',
            category: 'auth.sync',
            level: 'warning',
            data: { tokenName: name, error: error.message },
        });
    }
}

/**
 * Remove token from Capacitor Preferences in background (fire-and-forget)
 * @param {string} name - Token name
 */
async function removeFromCapacitorPreferences(name) {
    try {
        const capacitorAvailable = await isCapacitorPreferencesAvailable();
        if (capacitorAvailable && _capacitorPreferences) {
            const key = name === 'links' ? COOKIE_KEY_PREFIX + name : TOKEN_KEY_PREFIX + name;
            await _capacitorPreferences.remove({ key });
        }
    } catch (error) {
        // Silent failure - this is fire-and-forget background sync
        Sentry.addBreadcrumb({
            message: 'Background removal from Capacitor Preferences failed',
            category: 'auth.sync', 
            level: 'warning',
            data: { tokenName: name, error: error.message },
        });
    }
}

/**
 * Sync links to Capacitor Preferences in background (fire-and-forget)
 * @param {Object} links - Links object
 */
async function syncLinksToCapacitorPreferences(links) {
    try {
        const capacitorAvailable = await isCapacitorPreferencesAvailable();
        if (capacitorAvailable && _capacitorPreferences) {
            await _capacitorPreferences.set({
                key: COOKIE_KEY_PREFIX + 'links',
                value: JSON.stringify(links),
            });
        }
    } catch (error) {
        // Silent failure - this is fire-and-forget background sync
        Sentry.addBreadcrumb({
            message: 'Background sync of links to Capacitor Preferences failed',
            category: 'auth.sync',
            level: 'warning',
            data: { error: error.message },
        });
    }
}

/**
 * Store an authentication token with expiration
 * @param {string} name - Token name ('token', 'refresh', 'tfa_auth')
 * @param {string} token - Token value
 * @param {moment.Moment} expires - Token expiration date
 */
function setToken(name, token, expires) {
    if (_auth.storage === 'capacitor') {
        // ALWAYS use localStorage as primary storage (fast, synchronous)
        store.set(TOKEN_KEY_PREFIX + name, token, expires.toDate().getTime());
        
        // Also sync to Capacitor Preferences in background (fire-and-forget)
        syncToCapacitorPreferences(name, token, expires);
    } else if (_auth.storage === 'local') {
        store.set(TOKEN_KEY_PREFIX + name, token, expires.toDate().getTime());
    } else if (_auth.storage === 'memory') {
        _auth.tokens = _auth.tokens || {};
        _auth.tokens[name] = token;

        // Also set the localstorage, but we'll use a fallback.
        store.set(TOKEN_KEY_PREFIX + name, token, expires.toDate().getTime());
    } else {
        cookie.setItem(TOKEN_KEY_PREFIX + name, token, {
            secure: true,
            path: '/',
            expires: expires.toDate().toUTCString(),
        });
    }
}

/**
 * Retrieve an authentication token
 * @param {string} name - Token name ('token', 'refresh', 'tfa_auth')
 * @returns {string|null} Token value or null if not found/expired
 */
function getToken(name) {
    if (_auth.storage === 'capacitor') {
        // localStorage is primary and only source for capacitor storage
        return store.get(TOKEN_KEY_PREFIX + name);
    } else if (_auth.storage === 'local') {
        return store.get(TOKEN_KEY_PREFIX + name);
    } else if (_auth.storage === 'memory') {
        _auth.tokens = _auth.tokens || {};
        return _auth.tokens[name] || store.get(TOKEN_KEY_PREFIX + name);
    } else {
        return cookie.getItem(TOKEN_KEY_PREFIX + name);
    }
}

function removeToken(name) {
    if (_auth.storage === 'capacitor') {
        // ALWAYS remove from localStorage first (primary storage)
        store.remove(TOKEN_KEY_PREFIX + name);
        
        // Also remove from Capacitor Preferences in background (fire-and-forget)
        removeFromCapacitorPreferences(name);
    } else if (_auth.storage === 'local') {
        store.remove(TOKEN_KEY_PREFIX + name);
    } else if (_auth.storage === 'memory') {
        _auth.tokens = _auth.tokens || {};
        delete _auth.tokens[name];
        store.remove(TOKEN_KEY_PREFIX + name);
    } else {
        const expires = new Date(0).toUTCString();
        cookie.setItem(TOKEN_KEY_PREFIX + name, '', { expires, path: '/' });
    }
}


/**
 * Attempt to restore authentication tokens from Capacitor Preferences to localStorage
 * Used when localStorage is empty but we might have tokens in Preferences
 * @returns {Promise<boolean>} True if tokens were restored successfully
 */
async function restoreTokensFromCapacitorPreferences() {
    if (_auth.storage !== 'capacitor') {
        return false;
    }

    const capacitorAvailable = await isCapacitorPreferencesAvailable();
    if (!capacitorAvailable) {
        return false;
    }

    try {
        let restored = false;

        // Try to restore tokens from Preferences
        const tokenResult = await _capacitorPreferences.get({ key: TOKEN_KEY_PREFIX + 'token' });
        const refreshResult = await _capacitorPreferences.get({ key: TOKEN_KEY_PREFIX + 'refresh' });
        const linksResult = await _capacitorPreferences.get({ key: COOKIE_KEY_PREFIX + 'links' });

        // Restore token if valid and not expired
        if (tokenResult.value) {
            const tokenData = JSON.parse(tokenResult.value);
            if (tokenData.expires && Date.now() <= tokenData.expires) {
                store.set(TOKEN_KEY_PREFIX + 'token', tokenData.token, tokenData.expires);
                restored = true;
            }
        }

        // Restore refresh token if valid and not expired
        if (refreshResult.value) {
            const refreshData = JSON.parse(refreshResult.value);
            if (refreshData.expires && Date.now() <= refreshData.expires) {
                store.set(TOKEN_KEY_PREFIX + 'refresh', refreshData.token, refreshData.expires);
                restored = true;
            }
        }

        // Restore links
        if (linksResult.value) {
            const links = JSON.parse(linksResult.value);
            store.set(COOKIE_KEY_PREFIX + 'links', links);
            _auth.links = links;
            restored = true;
        }

        if (restored) {
            Sentry.addBreadcrumb({
                message: 'Restored auth tokens from Capacitor Preferences to localStorage',
                category: 'auth.restore',
                level: 'info',
            });
        }

        return restored;
    } catch (error) {
        Sentry.captureException(error, {
            tags: { component: 'AuthStore' },
            extra: { context: 'restoreTokensFromCapacitorPreferences' },
        });
        return false;
    }
}

function deauthorize() {
    const token = getToken('token'),
        refresh = getToken('refresh');

    if (!token && !refresh) {
        return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
        fetch(getConfig('users_api') + '/tokens/revoke', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json; schema=collection/jwt/1' },
            body: JSON.stringify({ tokens: [token, refresh].filter((v) => v) }),
        })
            .then((response) => {
                if (response.ok) {
                    response.json().then(resolve);
                } else {
                    response.json().then(reject);
                }
            })
            .catch(() => reject());
    });
}

var AuthStore = assign({}, EventEmitter.prototype, {
    emitChange: function () {
        this.emit(CHANGE_EVENT);
    },

    setStorage: function (storage, persist = false) {
        _auth.storage = storage;

        if (persist) {
            store.set('auth-storage-mode', storage);
        }
    },

    getStorage: function () {
        return _auth.storage;
    },

    addChangeListener: function (callback) {
        this.on(CHANGE_EVENT, callback);
    },

    removeChangeListener: function (callback) {
        this.removeListener(CHANGE_EVENT, callback);
    },

    getLinks: function () {
        const token = getToken('token');

        if (token) {
            if (_auth.storage === 'capacitor') {
                // localStorage is the only source for links in capacitor mode
                return store.get(COOKIE_KEY_PREFIX + 'links');
            } else {
                return _auth.links;
            }
        }

        _auth.links = null;
        return null;
    },

    getToken: function () {
        return getToken('token');
    },

    setToken: function (name, value, expires) {
        setToken(name, value, expires);
    },

    getRefresh: function () {
        return getToken('refresh');
    },

    getTfaAuth: function () {
        return getToken('tfa_auth');
    },

    restoreTokensFromCapacitorPreferences: restoreTokensFromCapacitorPreferences,

    isCapacitorPreferencesAvailable: isCapacitorPreferencesAvailable,

    fetch: function (url, request, anonOk = false, token = false, abortController = null, withResponseHeaders = false) {
        if (!token) {
            token = getToken('token');
        }

        // If we're not logged in, don't even bother firing the request, just reject it.
        if (!anonOk && !token) {
            return Promise.reject('not logged in');
        }

        // This saves a lot of boilerplate.
        if (!anonOk && (!url || (typeof url === 'object' && !url.url))) {
            return Promise.reject('probably also not logged in');
        }

        return new Promise((resolve, reject) => {
            // @todo - this could use some work - perhaps a URL object
            if (typeof url === 'object') {
                // Has a query param, hash or whatnot
                url =
                    url.url +
                    (url.query ? '?' + qs.stringify(url.query) : '') +
                    (url.fragment ? '#' + qs.stringify(url.fragment) : '');
            }

            // Make sure request is initalized
            request = request || {};

            // Initialize headers if not already initialized
            request.headers = request.headers || {};

            if (token) {
                request.headers.Authorization = 'bearer ' + token;
            }
            if (abortController) {
                request.signal = abortController.signal;
            }

            const urlObject = new URL(url);
            const path = urlObject.pathname;

            // Get the current active transaction, if it exists
            let transaction = Sentry.getCurrentHub().getScope().getTransaction();

            // If no active transaction, start a new one (optional)
            if (!transaction) {
                transaction = Sentry.startTransaction({
                    name: `${request.method || 'GET'} ${path}`,
                    op: 'http.client',
                });
            }
            const span = transaction.startChild({ op: 'http.client', description: `Fetching data from ${url}` });
            // Set transaction on scope to associate with errors and get included span instrumentation
            Sentry.getCurrentHub().configureScope((scope) => scope.setSpan(span));

            // Reads are idempotent, so retry them through transient failures. Writes get one attempt:
            // a network failure does not prove the server never processed the request. An aborted
            // signal makes the next attempt reject with AbortError, which is never retried.
            const method = (request.method || 'GET').toUpperCase();
            const doFetch = ['GET', 'HEAD'].includes(method) ? fetchWithRetry(url, request) : fetch(url, request);

            doFetch
                .then((response) => {
                    span.setHttpStatus(response.status);

                    if (abortController && abortController.signal.aborted) {
                        return;
                    }

                    // withResponseHeaders applies to both resolution and JSON rejection
                    const settle = withResponseHeaders
                        ? (json) => ({ body: json, headers: response.headers, status: response.status })
                        : (json) => json;

                    if (response.ok) {
                        // If we get a response back immediately, we're good
                        try {
                            response.json().then((json) => resolve(settle(json)), reject);
                        } catch (exp) {
                            Sentry.captureException(exp, span.getTraceContext());
                            reject(
                                <span>
                                    Oops! Something went wrong. Please try again or contact EatLove support at
                                    <a href="mailto:support@eatlove.is?subject=EatLove Crashed"> support@eatlove.is</a>
                                </span>
                            );
                        }

                        return;
                    }

                    var contentType = response.headers.get('content-type');

                    if (contentType && contentType.includes('application/json')) {
                        return response.json().then((json) => {
                            if (json && json.message === 'Forbidden' && token) {
                                // whoa, has our token been revoked? We need to reauthenticate.
                                // Log the user out so they can log back in.
                                AppDispatcher.handleViewAction({
                                    actionType: AuthConstants.AUTH_DEAUTHORIZE,
                                });

                                AppDispatcher.handleViewAction({
                                    actionType: UserConstants.USER_LOGOUT,
                                });

                                span.setData('response_json', json);
                                Sentry.captureMessage(json.message, span.getTraceContext());
                            } else {
                                MealStore.checkForMealsUpdateError(url, request, json);
                                GroceryStore.checkForGroceryUpdateError(url, request, json);
                            }

                            reject(settle(json));
                        });
                    }

                    if (contentType && contentType.includes('text/html')) {
                        response.text().then((text) => {
                            span.setData('text', text);
                            Sentry.captureMessage('Failed to Fetch', span.getTraceContext());
                            reject({ error: 'Failed to Fetch', text });
                        });

                        return;
                    }

                    Sentry.captureMessage('Failed to Fetch', span.getTraceContext());
                    return reject({ message: 'Failed to Fetch' });
                })
                .catch((exp) => {
                    if (exp.name === 'AbortError') {
                        return;
                    }

                    span.setStatus('unknown_error');
                    Sentry.captureException(exp, span.getTraceContext());

                    reject(
                        <span>
                            Oops! Something went wrong. Please try again or contact EatLove support at
                            <a href="mailto:support@eatlove.is?subject=EatLove Crashed"> support@eatlove.is</a>
                        </span>
                    );
                })
                .finally(() => {
                    span.finish();
                });
        });
    },
});

AppDispatcher.register(function (payload) {
    switch (payload.action.actionType) {
        case AuthConstants.AUTH_AUTHENTICATE:
            //eslint-disable-next-line no-case-declarations
            const { auth } = payload.action;

            // Set cookies/storage and local variables at the same time
            // Note: setToken is now async, but we don't await to maintain compatibility
            setToken('token', auth.token, moment(auth.token_expires));
            setToken('refresh', auth.refresh, moment(auth.refresh_expires));

            Sentry.addBreadcrumb({
                category: 'auth.issue',
                message: 'auth tokens stored',
                data: {
                    token: tokenClaims(auth.token),
                    token_expires: auth.token_expires,
                },
            });

            if (auth.tfa_auth) {
                setToken('tfa_auth', auth.tfa_auth, moment(auth.tfa_auth_expires));
            }

            _auth.links = auth.links;
            if (_auth.storage === 'capacitor') {
                // Store links in localStorage (primary)
                store.set(COOKIE_KEY_PREFIX + 'links', auth.links);
                // Sync to Capacitor Preferences in background (fire-and-forget)
                syncLinksToCapacitorPreferences(auth.links);
            } else {
                store.set(COOKIE_KEY_PREFIX + 'links', auth.links);
            }

            AuthStore.emitChange();
            break;

        case AuthConstants.AUTH_DEAUTHORIZE:
            // @todo - uncomment this line and it won't log you out each time there's a JS error on initial render.
            // return;

            /*
             * Remove the current user from Analytics before any cleaning.
             * Try to request the deauthorization of the current tokens (token and refreshtoken).
             * Whether or not we succeed in revoking the tokens, we clean the current user's data and guarantee the client logout.
             */
            Analytics.logout();
            deauthorize().finally(() => {
                _auth.links = null;
                // Delete the cookies too
                removeToken('token');
                removeToken('refresh');
                store.remove('auth-storage-mode');

                if (_auth.storage === 'capacitor') {
                    // Remove links from localStorage (primary)
                    store.set(COOKIE_KEY_PREFIX + 'links', null);
                    // Remove from Capacitor Preferences in background (fire-and-forget)
                    removeFromCapacitorPreferences('links');
                } else {
                    store.set(COOKIE_KEY_PREFIX + 'links', null);
                }

                AuthStore.emitChange();

                Sentry.setUser(null);

                SessionRecorder.stop();

                if (SessionRecorder.wasRecorded) {
                    window.location.reload();
                }
            });
            break;

        default:
            return;
    }
});

export default AuthStore;
